<template>
  <div class="navigate-by-keyword-container">
    <div class="add-keyword-list-mapping-button-container">
      <button type="button" class="btn btn-primary btn-rounded btn-ageinput add-keyword-list-mapping-button" @click="addMapping">
        <i class="fa fa-plus" aria-hidden="true"></i>
      </button>
      <span><strong>Add Mapping</strong></span>
    </div>
    
    <div v-for="(item, index) in keywordMappingList" :key="item.mappingUniqueId" class="navigate-by-keyword-mapping-container">
      <div>
        <span><strong>{{ item.mappingUniqueId || "keyword_set_1" }}</strong></span>
      </div>
      
      <!-- Conversation dropdown -->
      <div class="navigate-by-keyword-conversation-dropdown">
        <treeselect placeholder="Select conversation" :options="conversationOptions"
                    v-model="selectedConversation[index]" @input="(val) => getKeywords(val, index)" 
                    class="treeselect-box" />
        <div v-if="conversationInvalid[index]" class="error-message">
          <span class="text-danger">Conversation is required.</span>
        </div>
      </div>

      <!-- Keyword dropdown -->
      <div class="navigate-by-keyword-conversation-dropdown">
        <treeselect placeholder="Select keyword" :options="keywordOptions[index]" 
                    v-model="keywordsSelected[index]" :limit="2" :multiple="true" 
                    @input="(val) => selectKeywords(val, index)" class="treeselect-box" />
        <div v-if="keywordsInvalid[index]" class="error-message">
          <span class="text-danger">At least one keyword is required.</span>
        </div>                    
      </div>

      <!-- Remove Mapping Button -->
      <button type="button" class="btn btn-rounded btn-remove-nudge btn-nudge btn-danger" @click="removeMapping(index)">
        <i class="fa fa-minus" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
export default {
  name: 'NavigateToNextStageBasedOnKeyword',
  props: {
    functionName: String,
    conversationOptions: Array,
    stageOptions: Array,
    conversationNotExistError: Boolean,
    stageSelected: [Object, String, Number],
    keywordMappingList: Array,
    allKeywords: Array
  },
  data () {
    return {
      selectedConversation: [],
      keywordOptions: [],
      keywordsSelected: [],
      hasMounted: false,
      skipInitialSelectKeywords: true,
      conversationInvalid: [],
      keywordsInvalid: []
    }
  },
  mounted () {
    if (this.keywordMappingList && this.keywordMappingList.length > 0) {
      this.keywordMappingList.forEach((keywordMapping, mappingIndex) => {
        if (keywordMapping) {
          const selectedConvo = this.conversationOptions.find(conversation => {
            return conversation.conversation_id === keywordMapping.conversationId
          })
          if (selectedConvo) {
            this.selectedConversation.push(selectedConvo.id)
            this.keywordsSelected[mappingIndex] = keywordMapping.keywordsList
          }
        }
      })
    }
    this.hasMounted = true
    this.$nextTick(() => {
      this.skipInitialSelectKeywords = false
    })
  },
  methods: {
    validate () {
      let isConversationsValid = true
      let isKeywordsValid = true
      // Reset previous validation results
      this.conversationInvalid = []
      this.keywordsInvalid = []

      // Loop through each mapping and validate
      this.keywordMappingList.forEach((mapping, index) => {
        if (!mapping.conversationId) {
          this.$set(this.conversationInvalid, index, true)
          isConversationsValid = false
        } else {
          this.$set(this.conversationInvalid, index, false)
        }

        if (mapping.keywordsList.length === 0) {
          this.$set(this.keywordsInvalid, index, true)
          isKeywordsValid = false
        } else {
          this.$set(this.keywordsInvalid, index, false)
        }
      })
      return isConversationsValid && isKeywordsValid
    },
    removeMapping (index) {
      let keywordMappingList = cloneDeep(this.keywordMappingList)
      keywordMappingList.splice(index, 1)
      this.selectedConversation.splice(index, 1)
      this.keywordsSelected.splice(index, 1)
      this.keywordOptions.splice(index, 1)
      this.conversationInvalid.splice(index, 1)
      this.keywordsInvalid.splice(index, 1)
      keywordMappingList = keywordMappingList.map((mapping, i) => {
        return {
          ...mapping,
          mappingUniqueId: `keyword_set_${i + 1}`
        }
      })
      this.$emit('update-keyword-mapping-list', keywordMappingList)
    },
    selectKeywords (val, index) {
      this.$set(this.keywordsSelected, index, val)
      if (this.hasMounted && !this.skipInitialSelectKeywords) {
        this.$set(this.keywordsInvalid, index, val.length === 0)
        let newKeywordMappingList = []
        this.selectedConversation.forEach((eachSelectedConvo, selectedConvoIndex) => {
          const selectedConversation = this.conversationOptions.find(conversation => {
            return conversation.id === eachSelectedConvo
          })
          if (selectedConversation) {
            newKeywordMappingList.push({
              conversationId: selectedConversation.conversation_id,
              keywordsList: this.keywordsSelected[selectedConvoIndex],
              mappingUniqueId: this.keywordMappingList[selectedConvoIndex].mappingUniqueId
            })
          }
          if (selectedConversation === undefined) {
            newKeywordMappingList.push({
              conversationId: null,
              keywordsList: [],
              mappingUniqueId: `keyword_set_${selectedConvoIndex + 1}`
            })
          }
        })
        this.$emit('update-keyword-mapping-list', newKeywordMappingList)
      }
    },
    getKeywords (val, mappingIndex) {
      const selectedConversation = this.conversationOptions[val]
      if (selectedConversation) {
        this.$set(this.conversationInvalid, mappingIndex, false) // Mark conversation valid
        const options = this.allKeywords
          .filter(k => k.conversation_id === selectedConversation.conversation_id)
          .map(k => ({ id: k.keyword_id, label: k.keyword }))

        this.$set(this.keywordOptions, mappingIndex, options)
        const selectedConversationId = this.keywordMappingList[mappingIndex].conversationId
        if (selectedConversation.conversation_id !== selectedConversationId) {
          this.selectKeywords([], mappingIndex)
        }
      } else {
        this.$set(this.keywordOptions, mappingIndex, [])
        this.$set(this.keywordsSelected, mappingIndex, [])
      }
    },
    addMapping () {
      const newMapping = {
        conversationId: null,
        keywordsList: [],
        mappingUniqueId: `keyword_set_${this.keywordMappingList.length + 1}`
      }
      this.$emit('add-keyword-mapping', newMapping)
    },
    // Check if conversation is invalid
    isConversationInvalid (index) {
      const mapping = this.keywordMappingList[index]
      return !mapping.conversationId
    },
    // Check if keywords are invalid
    isKeywordsInvalid (index) {
      const mapping = this.keywordMappingList[index]
      return mapping.keywordsList.length === 0
    }
  }
}
</script>

<style scoped>

.navigate-by-keyword-error-text{
  color: red;
}

.navigate-by-keyword-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.navigate-by-keyword-mapping-container {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.add-keyword-list-mapping-button-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;
}

.add-keyword-list-mapping-button {
  margin-right: 8px;
}

.treeselect-box {
  flex: 1 1 300px; /* Ensures they take equal width, adjust as needed */
  min-width: 250px;
}

.error-messages {
  flex-basis: 100%;
  margin-top: 4px;
}

.text-red {
  color: red;
  font-size: 0.85rem;
}
</style>


// WEBPACK FOOTER //
// pf-navigate-by-keyword.vue?4b97b503